import { Flex, Form, Input, Select } from 'antd';
import invariant from 'tiny-invariant';
import { useTranslation } from 'translations/hooks';

import { useGraphqlApiHeaders } from 'business/contextProviders/useApiHeaders';
import { useAppContext } from 'business/contextProviders/useAppContext';
import { SteeringTableFilterData } from 'business/steering/pages/table-page';
import {
  useSteeringChartFilters,
  useSteeringChartFiltersFormName,
} from 'business/steering/providers/steering-chart-filters-provider';
import {
  useSteeringFilters,
  useSteeringFiltersFormName,
} from 'business/steering/providers/steering-filters-provider';
import { Position } from 'business/steering/providers/steering-filters-provider/types';
import { Module_Enum, useGetCurrentRingQuery } from 'generated/graphql';
import { FilterData } from 'technical/filter-data';
import { SelectedItemsFooter } from 'ui/filter-footer';
import {
  PreselectRingRangeInput,
  PreselectRingRangeTypeEnum,
} from 'ui/form/preselect-ring-range-input';

export const SteeringTableMobileFilters = ({
  showGraph,
  filterData,
}: {
  showGraph: boolean;
  filterData: FilterData<SteeringTableFilterData>;
}) => {
  const { currentConstructionSite } = useAppContext();
  invariant(currentConstructionSite, 'No construction site');

  const { t } = useTranslation();
  const formName = useSteeringFiltersFormName();
  const filters = useSteeringFilters();
  const [form] = Form.useForm();
  const chartFormName = useSteeringChartFiltersFormName();
  const chartFilters = useSteeringChartFilters();
  const [chartForm] = Form.useForm();
  const { currentConstructionSiteId } = useAppContext();
  const headers = useGraphqlApiHeaders(Module_Enum.Steering);

  const { data, error } = useGetCurrentRingQuery({
    variables: { constructionSiteId: currentConstructionSiteId },
    context: {
      headers,
    },
  });

  return (
    <div>
      <Flex wrap>
        <Form
          layout="inline"
          form={form}
          name={formName}
          initialValues={filters}
        >
          {/* Hidden field used to set the filter type to `ring` */}
          <Form.Item name="filterType" hidden>
            <Input disabled />
          </Form.Item>
          <Form.Item name="ringRange" label={t('input.ringRange.label')}>
            <PreselectRingRangeInput
              defaultType={PreselectRingRangeTypeEnum.All}
              lastRing={
                data?.parameter_ring_aggregate.aggregate?.max?.ringNumber
              }
              error={!!error}
            />
          </Form.Item>
        </Form>
        {showGraph ? (
          <Form
            layout={'inline'}
            form={chartForm}
            name={chartFormName}
            initialValues={chartFilters}
          >
            <Form.Item
              name="position"
              label={t('steering.filters.position.label')}
            >
              <Select<Position>
                popupMatchSelectWidth={false}
                options={
                  [
                    {
                      label: t('steering.filters.position.rear'),
                      value: 'rear',
                    },
                    {
                      label: t('steering.filters.position.front'),
                      value: 'front',
                    },
                    ...(currentConstructionSite.isArticulated
                      ? [
                          {
                            label: t('steering.filters.position.cuttingWheel'),
                            value: 'cuttingWheel',
                          } as const,
                        ]
                      : []),
                  ] satisfies { label: string; value: Position }[]
                }
              />
            </Form.Item>
            <Form.Item name="scale" label={t('steering.filters.scale')}>
              <Select
                options={[
                  { value: 50 },
                  { value: 100 },
                  { value: 200 },
                  { value: 500 },
                  { value: 1000 },
                ]}
              />
            </Form.Item>
          </Form>
        ) : null}
      </Flex>
      {filters.filterType === 'ring' &&
      filters.ringRange?.every((boundary) => boundary === null) &&
      filterData?.selectedRings ? (
        <SelectedItemsFooter mobile>
          {t('productionAndPerformance.ring.selectedRings', {
            selectedRings: filterData?.selectedRings,
          })}
        </SelectedItemsFooter>
      ) : null}
    </div>
  );
};
